import convert from 'color-convert';
import { useBrandingStore } from '~/stores/branding/branding-store';
import { useButtonItemsStore } from '~/stores/branding/button-items-store';
import type {
  StyleConfig,
  StyleMappings,
  PrimaryButtonsStyles,
  SecondaryButtonsStyles,
  LinkStyles
} from '~/types/branding/button-items';

export const useBrandingButtonLink = () => {
  const { partnerConfig } = storeToRefs(useBrandingStore());
  const {
    primaryButtonStyles,
    prodPrimaryButtonStyles,
    prodSecondaryButtonStyles,
    prodLinkStyles,
    secondaryButtonStyles,
    linkStyles,
    buttonLinkItemsState
  } = storeToRefs(useButtonItemsStore());

  const styleMappings: StyleMappings = {
    primary: {
      backgroundColor: {
        key: 'button-background-color',
        defaultValue: () => primaryHSLToHEX()
      },
      backgroundColorHover: {
        key: 'button-background-color-hover',
        defaultValue: () => primaryHSLToHEX('20')
      },
      textColor: {
        key: 'button-text-color',
        defaultValue: '#FFFFFF'
      },
      textColorHover: {
        key: 'button-text-color-hover',
        defaultValue: '#FFFFFF'
      }
    },
    secondary: {
      backgroundColor: {
        key: 'button-background-color',
        defaultValue: '#FFFFFF'
      },
      backgroundColorHover: {
        key: 'button-background-color-hover',
        defaultValue: () => primaryHSLToHEX('90')
      },
      textColor: {
        key: 'button-text-color',
        defaultValue: () => primaryHSLToHEX()
      },
      textColorHover: {
        key: 'button-text-color-hover',
        defaultValue: () => primaryHSLToHEX()
      },
      borderColor: {
        key: 'button-border-color',
        defaultValue: () => primaryHSLToHEX()
      },
      borderColorHover: {
        key: 'button-border-color-hover',
        defaultValue: () => primaryHSLToHEX()
      }
    },
    link: {
      textColor: {
        key: 'text-color',
        defaultValue: () => primaryHSLToHEX()
      },
      textColorHover: {
        key: 'text-color-hover',
        defaultValue: () => primaryHSLToHEX('20')
      }
    }
  };

  const primaryHSLToHEX = (variant: string | undefined = undefined) => {
    const primaryHSL = convert.hex.hsl(
      partnerConfig.value?.colors.primary.substring(1) ?? '335584'
    );
    switch (variant) {
      case '20':
        return `#${convert.hsl.hex([primaryHSL[0], primaryHSL[1], (primaryHSL[2] + 15) / 2])}`;
      case '90':
        return `#${convert.hsl.hex([primaryHSL[0], primaryHSL[1], 98])}`;
      default:
        return partnerConfig.value?.colors.primary ?? '#335584';
    }
  };

  const resetButtonLinkItemsColors = () => {
    transformButtonLinkItemsStyles('primary', primaryButtonStyles, prodPrimaryButtonStyles, true);
    transformButtonLinkItemsStyles(
      'secondary',
      secondaryButtonStyles,
      prodSecondaryButtonStyles,
      true
    );
    transformButtonLinkItemsStyles('link', linkStyles, prodLinkStyles, true);
    buttonLinkItemsState.value = 'default';
  };

  const transformButtonLinkItemsStyles = <T extends keyof StyleMappings>(
    styleType: T,
    targetRef: Ref<
      StyleMappings[T] extends Record<string, StyleConfig>
        ? { [K in keyof StyleMappings[T]]: string }
        : never
    >,
    prodRef: Ref<
      StyleMappings[T] extends Record<string, StyleConfig>
        ? { [K in keyof StyleMappings[T]]: string }
        : never
    >,
    useDefaults = false
  ): void => {
    const mapping = styleMappings[styleType];
    const transformed: any = {};

    for (const [prop, config] of Object.entries(mapping)) {
      const defaultValue =
        typeof config.defaultValue === 'function' ? config.defaultValue() : config.defaultValue;
      const value = useDefaults
        ? defaultValue
        : partnerConfig.value?.styles?.[`${styleType}-${config.key}`];
      transformed[prop] = value ?? defaultValue;
    }

    targetRef.value = transformed;
    prodRef.value = { ...transformed };
  };

  const flattenButtonLinkItemsStyles = (
    prefix: string,
    styles: PrimaryButtonsStyles | SecondaryButtonsStyles | LinkStyles
  ) => {
    const mapping: Record<string, string> = {};
    Object.entries(styles).forEach(([key, value]) => {
      if (value !== undefined) {
        const cssKey = key.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
        mapping[`${prefix}-${cssKey}`] = value;
      }
    });
    return mapping;
  };

  return {
    transformButtonLinkItemsStyles,
    resetButtonLinkItemsColors,
    flattenButtonLinkItemsStyles
  };
};
